import {
  adjustToTimeZone,
  PipelineEntity,
  pipelineStatisticsSelector,
  StatisticsSearchCriteria,
  TransportCategory,
  useSearchStatisticsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useDispatch} from "react-redux";
import {useOtsUISelector} from "../../../hooks/useOtsUISelector";
import {skipToken} from "@reduxjs/toolkit/query";
import {selectPipelineUIFilter} from "../../../store/entity/pipeline/selectors";
import {setUIPipelineFilter} from "../../../store/entity/pipeline/actions";
import {EntityInsightsView} from "../common/EntityInsightsView/EntityInsightsView";
import {PipelineInsightsGrid} from "../../insights/PipelineInsightsGrid/PipelineInsightsGrid";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

export type PipelineInsightsProps = {
  pipeline: PipelineEntity
  category?: TransportCategory
}

export const PipelineInsights = (props: PipelineInsightsProps) => {
  const {pipeline, category} = props;

  const dispatch = useDispatch()

  const {regionId} = useApp()
  const filter = useOtsUISelector(state => selectPipelineUIFilter(state, pipeline.id))

  const criteria: StatisticsSearchCriteria | any = useMemo(() => {
    if (filter && regionId) {
      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: adjustToTimeZone(filter.toDttm).getTime(),
        regionIds: [regionId],
        pipelineIds: [pipeline.id],
        includeSubTruckCompanies: true,
        includeTruckingData: false,
        includePipelineData: true,
        includeContainerVolumeInventoryData: true,
        categories: category ? [category.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [filter, regionId, pipeline.id, category])

  const selectStatistics = useMemo(() => {
    return pipelineStatisticsSelector()
  }, []);

  const {stats, isLoading} = useSearchStatisticsQuery(criteria, {
    selectFromResult: (result) => ({
      ...result,
      stats: selectStatistics(result, pipeline.id),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    dispatch(setUIPipelineFilter({
      id: pipeline.id,
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime()
    }));
  }, [dispatch, pipeline.id]);

  return (
    <EntityInsightsView
      onSubmit={onFilterChange}
      form={{
        defaultValues: {
          fromDate: filter ? new Date(filter.fromDttm) : new Date(),
          toDate: filter?.toDttm ? new Date(filter.toDttm) : undefined,
        }
      }}>
      <PipelineInsightsGrid
        insights={stats}
        loading={isLoading}
        rateModels={['Pipeline']}/>
    </EntityInsightsView>
  );
};

